import React from "react";

import { useFetchConnectedIntegrationAccounts } from "./useFetchConnectedIntegration";

import type { IntegrationScope } from "../constants";
import type { Integration } from "../types/integrations";
import type { ModalStep } from "../types/modal";

interface UseModalStepDeterminationProps {
  integration: Integration | null;
  isOpen: boolean;
  initialStep?: ModalStep;
  integrationScope?: IntegrationScope;
  /*
  Pins the opening step, overriding what the connection status would imply. Needed to
  connect another account while one is already connected, which would otherwise resolve
  to the manage step.
  */
  startStep?: ModalStep;
}

export function useModalStepDetermination({
  integration,
  isOpen,
  initialStep,
  integrationScope,
  startStep,
}: UseModalStepDeterminationProps) {
  const [currentStep, setCurrentStep] = React.useState<ModalStep>(
    initialStep || "authenticate",
  );

  // Use ref to track if we've set the initial step from API
  const hasSetInitialStepRef = React.useRef(false);

  // Only fetch when modal is open and integration exists
  const {
    data: connectedAccounts = [],
    isLoading,
    error,
  } = useFetchConnectedIntegrationAccounts(integration?.label || "", {
    integrationScope,
  });

  // Determine step based on connection status when data arrives
  React.useEffect(() => {
    if (isOpen && integration && !hasSetInitialStepRef.current) {
      if (startStep) {
        setCurrentStep(startStep);
        hasSetInitialStepRef.current = true;
      } else if (
        // If an explicit initial step is provided (like "result", "crawler_preference" from URL), respect it immediately
        initialStep &&
        (initialStep === "result" || initialStep === "crawler_preference")
      ) {
        setCurrentStep(initialStep);
        hasSetInitialStepRef.current = true;
      } else if (!isLoading) {
        // Otherwise, wait for API response and determine step based on connection status
        const determinedStep =
          connectedAccounts.length > 0 ? "manage" : "authenticate";
        setCurrentStep(determinedStep);
        hasSetInitialStepRef.current = true;
      }
    }
  }, [
    isOpen,
    integration,
    connectedAccounts,
    isLoading,
    initialStep,
    startStep,
  ]);

  // Reset when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      hasSetInitialStepRef.current = false;
      // Reset to initial step when modal closes
      setCurrentStep(initialStep || "authenticate");
    }
  }, [isOpen, initialStep]);

  // Manual step control
  const setStep = React.useCallback((step: ModalStep) => {
    setCurrentStep(step);
  }, []);

  return {
    currentStep,
    isLoading:
      isLoading &&
      !hasSetInitialStepRef.current &&
      isOpen &&
      !startStep &&
      !(initialStep === "result" || initialStep === "crawler_preference"),
    error,
    setStep,
  };
}
